import { cva } from "class-variance-authority";
import type { ComponentPropsWithoutRef, MouseEvent } from "react";
import { Icon } from "../icon";
import { Kbd } from "../kbd";
import { Tooltip } from "../tooltip";

const sidebarToggleExpandedStyles = cva([
  "inline-flex",
  "h-(--spacing-40)",
  "w-full",
  "min-w-0",
  "items-center",
  "justify-between",
  "gap-(--spacing-8)",
  "rounded-(--radius-sm)",
  "border-0",
  "bg-transparent",
  "px-(--spacing-10)",
  "py-(--spacing-12)",
  "text-left",
  "font-(family-name:--font-family-label)",
  "font-(number:--font-weight-medium)",
  "text-(length:--font-size-label-large)",
  "leading-(--font-line-height-24)",
  "tracking-(--font-letter-spacing-default)",
  "text-menu-icon-default",
  "outline-none",
  "transition-colors",
  "duration-200",
  "ease-in-out",
  "hover:bg-menu-item-hover",
  "active:bg-menu-item-pressed",
  "focus-visible:ring-2",
  "focus-visible:ring-inset",
  "focus-visible:ring-menu-focus-ring",
  "max-[599px]:hidden",
]);
const sidebarToggleCollapsedStyles = cva([
  "inline-flex",
  "size-(--spacing-40)",
  "items-center",
  "justify-between",
  "rounded-(--radius-sm)",
  "border-0",
  "bg-transparent",
  "px-(--spacing-10)",
  "py-(--spacing-12)",
  "text-menu-icon-default",
  "outline-none",
  "transition-colors",
  "duration-200",
  "ease-in-out",
  "hover:bg-menu-item-hover",
  "active:bg-menu-item-pressed",
  "focus-visible:ring-2",
  "focus-visible:ring-inset",
  "focus-visible:ring-menu-focus-ring",
]);

export type SidebarCollapseDirection = "left" | "right";

const sidebarToggleIcons = {
  left: {
    collapse: "arrow-left-shrink-sidebar",
    expand: "arrow-right-expand-sidebar",
  },
  right: {
    collapse: "arrow-right-expand-sidebar",
    expand: "arrow-left-shrink-sidebar",
  },
} as const;

export interface SidebarToggleProps
  extends Omit<
    ComponentPropsWithoutRef<"button">,
    "aria-expanded" | "aria-label" | "children" | "className" | "onToggle"
  > {
  /** Whether the associated sidebar is currently collapsed. */
  collapsed: boolean;
  /** Called when the user requests a change to the sidebar state. */
  onToggle: () => void;
  /** Direction in which the sidebar collapses. Defaults to "left". */
  collapseDirection?: SidebarCollapseDirection;
  /** Optional keyboard shortcut displayed in the collapsed-state tooltip. */
  shortcut?: string;
}

/** Controlled presentation for expanding and collapsing a desktop sidebar. */
export function SidebarToggle({
  collapsed,
  collapseDirection = "left",
  onClick,
  onToggle,
  shortcut,
  ...props
}: SidebarToggleProps) {
  const label = collapsed ? "Expand Panel" : "Collapse Panel";
  const iconName =
    sidebarToggleIcons[collapseDirection][collapsed ? "expand" : "collapse"];

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onClick?.(event);
    onToggle();
  }

  if (!collapsed) {
    return (
      <button
        {...props}
        aria-expanded
        aria-label={label}
        className={sidebarToggleExpandedStyles()}
        onClick={handleClick}
        type="button"
      >
        <span className="flex min-w-0 flex-1 items-center gap-(--spacing-8)">
          <Icon aria-hidden name={iconName} size="sm" tone="inherit" />
          <span className="min-w-0 truncate">Collapse Panel</span>
        </span>
      </button>
    );
  }

  return (
    <span className="max-[599px]:hidden">
      <Tooltip
        align="center"
        endSection={
          shortcut ? <Kbd aria-hidden="true">{shortcut}</Kbd> : undefined
        }
        label="Expand"
        showArrow={false}
        side="right"
      >
        <button
          {...props}
          aria-expanded={false}
          aria-label={label}
          className={sidebarToggleCollapsedStyles()}
          onClick={handleClick}
          type="button"
        >
          <Icon aria-hidden name={iconName} size="sm" tone="inherit" />
        </button>
      </Tooltip>
    </span>
  );
}
